import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Leaf, ArrowLeft, Loader2, Share2, Twitter, Facebook, 
  Copy, Check, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SITE_URL = window.location.origin;

const SharedDesignPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    loadSharedDesign();
  }, [shareId]);
  
  const loadSharedDesign = async () => {
    try {
      const response = await axios.get(`${API}/shared-designs/${shareId}`);
      setDesign(response.data);
    } catch (err) {
      setError("This design is no longer available or the link is invalid.");
    } finally {
      setLoading(false);
    }
  };
  
  const getCategoryIcon = (category) => {
    const icons = {
      vegetable: "🥬",
      herb: "🌿",
      fruit: "🍎",
      flower: "🌸",
      tree: "🌳"
    };
    return icons[category] || "🌱";
  };
  
  const shareUrl = `${SITE_URL}/shared/${shareId}`;
  const shareText = design ? `Check out my garden design "${design.name}" on RootSpace!` : "";
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };
  
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "width=600,height=400");
  };
  
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "width=600,height=400");
  };
  
  const shareToPinterest = () => {
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "width=600,height=400");
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-amber-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-amber-50 p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Leaf className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Design Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate("/")} data-testid="go-home-btn">
              Go to RootSpace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              RootSpace
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn">
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? "Copied!" : "Copy Link"}
            </Button>
            <Button variant="outline" size="sm" onClick={shareToTwitter} data-testid="share-twitter-btn">
              <Twitter className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={shareToFacebook} data-testid="share-facebook-btn">
              <Facebook className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={shareToPinterest} data-testid="share-pinterest-btn">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
              </svg>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Design Info */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="design-title">
              {design.name}
            </h1>
            <p className="text-muted-foreground">
              Created by {design.owner_name}
            </p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <Badge variant="secondary">
                {design.canvas_width_feet} × {design.canvas_height_feet} ft
              </Badge>
              <Badge variant="secondary">
                {design.plants?.length || 0} plants
              </Badge>
            </div>
          </div>

          {/* Canvas Preview */}
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div
                ref={canvasRef}
                className="relative w-full bg-green-50"
                style={{
                  paddingTop: `${(design.canvas_height_feet / design.canvas_width_feet) * 100}%`,
                  maxHeight: "600px"
                }}
                data-testid="shared-canvas"
              >
                {/* Background Image */}
                {design.background_image && (
                  <img
                    src={design.background_image}
                    alt="Garden background"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                )}
                
                {/* Grid Overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)",
                    backgroundSize: `${100 / design.canvas_width_feet}% ${100 / design.canvas_height_feet}%`
                  }}
                />
                
                {/* Plants */}
                {design.plants?.map(plant => (
                  <div
                    key={plant.id}
                    className="absolute"
                    style={{
                      left: `${plant.x}%`,
                      top: `${plant.y}%`,
                      transform: "translate(-50%, -50%)"
                    }}
                  >
                    <div
                      className="rounded-full p-2 shadow-lg"
                      style={{ backgroundColor: (plant.color || "#4CAF50") + "40" }}
                    >
                      <span className="text-2xl">{plant.icon || "🌱"}</span>
                    </div>
                    <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      <span className="text-xs font-medium bg-white/90 px-1 rounded shadow">
                        {plant.plant_name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Plant List */}
          {design.plants?.length > 0 && (
            <Card className="mt-6">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Plants in this design</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {design.plants.map(plant => (
                    <div
                      key={plant.id}
                      className="flex items-center gap-2 p-2 rounded-lg bg-green-50"
                    >
                      <span className="text-xl">{plant.icon || "🌱"}</span>
                      <div>
                        <p className="text-sm font-medium">{plant.plant_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {plant.spacing_inches}&quot; spacing
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <div className="text-center mt-8">
            <p className="text-muted-foreground mb-4">
              Want to create your own garden design?
            </p>
            <Button onClick={() => navigate("/design")} size="lg" data-testid="create-own-btn">
              <Leaf className="w-4 h-4 mr-2" />
              Create Your Own Design
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default SharedDesignPage;
